/**
 * One endpoint, seen through all six windows at once.
 *
 * The table answers "which endpoints are bad right now", for one window at a
 * time. That leaves the question the table cannot answer: *is this new?* A 4%
 * error rate is a crisis on an endpoint that was at 0.1% all week and a Tuesday
 * on one that has been at 4% for a month, and the number itself looks identical
 * either way. So this screen puts the six windows side by side — 30m, 6h, 12h,
 * 24h, 72h, 7d, recent on the left — and lets the comparison do the work.
 *
 * Three refusals shape the layout:
 *
 * - A window the backend has not cached yet is **not** drawn as zero. The API
 *   only returns windows already in cache (opening a drill-down must never fan
 *   out into six Cloudflare queries — see `endpoint_detail` in
 *   `app/routes/metrics.py`), so a missing window means "not measured yet", and
 *   it says exactly that.
 * - `total === null` stays distinct from `0`, as everywhere else on this
 *   dashboard.
 * - `traffic_cliff` is only rendered on the windows where it means anything.
 *   The backend compares against a 24h baseline, and the windows are nested, so
 *   the flag is structurally `false` at 24h/72h/7d. Printing "steady" there
 *   would be answering a question nobody asked.
 */
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { ApiError, api } from '../api/client'
import { DEFAULT_WINDOW, WINDOW_IDS, WINDOW_LABEL_KEYS, WINDOW_REFRESH_MS } from '../api/types'
import type { EndpointDetailResponse, EndpointSummary, WindowId } from '../api/types'
import { useI18n } from '../i18n'
import { CLIFF_WINDOWS } from './EndpointRow'
import { FreshnessBadge } from './FreshnessBadge'
import { HealthBadge } from './HealthBadge'
import { StatusRatioBar } from './StatusRatioBar'
import { StatusPie } from './StatusPie'
import { StatusTimeSeries } from './StatusTimeSeries'
import { numberFormat, percent } from './statusColors'

export function endpointDetailQueryKey(endpointId: string, window: WindowId) {
  return ['endpoint', endpointId, window] as const
}

export interface EndpointDetailProps {
  endpointId: string
  /** The window the rest of the page is pinned to. It is the only one this
   *  request may refresh; the other five come from cache. Optional so the
   *  component still matches the plan's `<EndpointDetail endpointId/>`; the
   *  dashboard threads its own window through so the two cannot disagree. */
  window?: WindowId
  /** Rendered as a close button when supplied. */
  onClose?: () => void
}

export function EndpointDetail({
  endpointId,
  window = DEFAULT_WINDOW,
  onClose,
}: EndpointDetailProps) {
  const { t } = useI18n()
  const { data, error, isPending } = useQuery({
    queryKey: endpointDetailQueryKey(endpointId, window),
    queryFn: () =>
      api.get<EndpointDetailResponse>(`/api/v1/endpoints/${encodeURIComponent(endpointId)}`, {
        window,
      }),
    staleTime: WINDOW_REFRESH_MS[window],
    refetchInterval: WINDOW_REFRESH_MS[window],
    refetchOnWindowFocus: true,
  })

  // Which window the pie describes. Seeded from the dashboard's window but
  // independent of it: the point of the tiles is to compare 30m against 7d
  // without changing what the whole page is showing.
  const [pieWindow, setPieWindow] = useState<WindowId>(window)

  if (isPending) return <DetailSkeleton onClose={onClose} />
  if (error) return <DetailError error={error} onClose={onClose} />

  const { endpoint, windows, meta } = data

  return (
    <section aria-label={`${endpoint.method} ${endpoint.path}`} className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="num text-xs font-semibold text-muted">{endpoint.method}</span>
            <span className="num break-all text-lg text-fg">{endpoint.path}</span>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-xs text-muted">
            <span>
              {t('domain.family')} <span className="text-fg">{endpoint.family}</span>
            </span>
            <HealthBadge state={endpoint.health} />
            {/*
              The window name is looked up as a KEY, never as English text, so
              the switch re-renders it and a key that does not exist is a build
              failure rather than an English fragment inside a Chinese clause.
              `toLowerCase` sentence-cases the English ("in the last 24 hours")
              and is a no-op on Chinese.
            */}
            <span title={t('dashboard.detail.healthWindowHint')}>
              {t('dashboard.detail.inWindow', {
                window: t(WINDOW_LABEL_KEYS[window]).toLowerCase(),
              })}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <FreshnessBadge meta={meta} />
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-border px-1.5 py-0.5 text-xs text-muted hover:text-fg"
            >
              {t('common.close')}
            </button>
          )}
        </div>
      </header>

      {/*
        auto-fit against the CONTAINER, not `lg:grid-cols-6` against the
        viewport. Those are the same thing only while this sits full-width;
        inside the 26rem side panel a wide viewport still matched `lg` and
        squeezed six tiles into ~62px each, clipping "2,402,628" to "2,402".
      */}
      <ul className="grid grid-cols-[repeat(auto-fit,minmax(6rem,1fr))] gap-2">
        {WINDOW_IDS.map((id) => (
          <WindowCard
            key={id}
            id={id}
            summary={windows[id]}
            current={id === pieWindow}
            onSelect={setPieWindow}
          />
        ))}
      </ul>

      <StatusPie window={pieWindow} summary={windows[pieWindow]} />

      <StatusTimeSeries windows={windows} />
    </section>
  )
}

/* ------------------------------------------------------------------ */

function WindowCard({
  id,
  summary,
  current,
  onSelect,
}: {
  id: WindowId
  summary: EndpointSummary | undefined
  current: boolean
  /** Picks the window the pie below describes. */
  onSelect: (id: WindowId) => void
}) {
  const { t } = useI18n()
  return (
    <li
      data-testid={`window-card-${id}`}
      data-window={id}
      // `aria-current` is omitted, not set to "false": the attribute exists to
      // point at one thing.
      {...(current ? { 'aria-current': 'true' as const } : {})}
      className="contents"
    >
      {/*
        A button, not a static card: the whole tile is the target, because a
        6rem tile with a small hit area inside it is a worse click than the
        tile itself. `text-left` undoes the button default so the numbers stay
        aligned with the rest of the panel.
      */}
      <button
        type="button"
        onClick={() => onSelect(id)}
        aria-pressed={current}
        title={t('dashboard.detail.windowCardTitle', {
          window: t(WINDOW_LABEL_KEYS[id]),
        })}
        className={`w-full space-y-2 rounded-md border bg-surface p-2 text-left transition-colors
                    hover:border-accent/60 focus:border-accent focus:outline-none ${
                      current ? 'border-accent ring-1 ring-inset ring-accent/30' : 'border-border'
                    }`}
      >
      <div className="flex items-baseline justify-between gap-2">
        <span className="num text-xs font-semibold text-fg">{id}</span>
        {summary && <HealthBadge state={summary.health} compact />}
      </div>

      {summary === undefined ? (
        // The backend serves only windows it has already fetched. Silence here
        // is "we have not looked yet", which is not a number.
        <p
          className="rounded bg-hatch px-1 py-2 text-center text-xs text-muted"
          title={t('dashboard.detail.notCachedTitle')}
        >
          {t('dashboard.detail.notCached')}
        </p>
      ) : (
        <>
          <div>
            <div className="num text-lg text-fg">
              {summary.total === null ? (
                <span title={t('dashboard.detail.noDataTitle')} className="text-muted">
                  —
                </span>
              ) : (
                numberFormat.format(summary.total)
              )}
            </div>
            <div className="text-xs text-muted">{t('dashboard.detail.requestsLabel')}</div>
          </div>

          <StatusRatioBar byStatus={summary.by_status} total={summary.total} />

          <div className="flex items-baseline justify-between gap-2 text-xs">
            <span className="text-muted">{t('dashboard.detail.errorsLabel')}</span>
            <span className="num text-fg">
              {summary.error_rate === null ? (
                <span className="text-muted">—</span>
              ) : (
                percent(summary.error_rate)
              )}
            </span>
          </div>

          {CLIFF_WINDOWS.has(id) && summary.traffic_cliff && (
            <p
              title={t('dashboard.detail.trafficCliffTitle')}
              className="rounded bg-degraded/15 px-1 py-0.5 text-center text-xs font-medium text-degraded"
            >
              {t('dashboard.detail.trafficCliff')}
            </p>
          )}
        </>
      )}
      </button>
    </li>
  )
}

function DetailSkeleton({ onClose }: { onClose?: () => void }) {
  const { t } = useI18n()
  return (
    <section aria-label={t('dashboard.detail.regionLabel')} aria-busy="true" className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="h-6 w-72 animate-pulse rounded bg-border" />
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border px-1.5 py-0.5 text-xs text-muted hover:text-fg"
          >
            {t('common.close')}
          </button>
        )}
      </div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(6rem,1fr))] gap-2">
        {WINDOW_IDS.map((id) => (
          <div key={id} className="h-32 animate-pulse rounded-md bg-border" />
        ))}
      </div>
    </section>
  )
}

/** A 404 here is a real answer — the endpoint id is not in the registry, which
 *  usually means the spec refresh dropped it — so it gets its own sentence
 *  rather than a generic failure. */
function DetailError({ error, onClose }: { error: unknown; onClose?: () => void }) {
  const { t } = useI18n()
  // `HTTP 503` stays as it is: a status line is a technical token, not copy.
  const detail =
    error instanceof ApiError
      ? error.detail || `HTTP ${error.status}`
      : t('dashboard.detail.unreachable')
  const gone = error instanceof ApiError && error.status === 404

  return (
    <section aria-label={t('dashboard.detail.regionLabel')} className="space-y-2">
      <p role="alert" className="rounded-md bg-down/10 px-3 py-2 text-sm text-down">
        {gone
          ? t('dashboard.detail.gone', { detail })
          : t('dashboard.detail.unavailable', { detail })}
      </p>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="rounded border border-border px-1.5 py-0.5 text-xs text-muted hover:text-fg"
        >
          {t('common.close')}
        </button>
      )}
    </section>
  )
}
